import * as React from 'react';
import { createPortal } from 'react-dom';
import { ArrowDownAZ, ArrowUpAZ, Search } from 'lucide-react';
import { cn, compararNatural, numero } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';

/**
 * O filtro de coluna do Excel: os valores distintos da coluna com checkbox,
 * busca entre eles, "selecionar tudo" e Ordenar A→Z / Z→A. Só vale ao clicar
 * em Aplicar, como no Excel.
 *
 * A lista sai das linhas que os filtros das OUTRAS colunas deixaram — com a
 * contagem de cada valor.
 */

const VAZIO = '(vazio)';
/** Acima disso a lista pede para refinar pela busca: milhares de checkboxes não ajudam ninguém. */
const MAXIMO_NA_LISTA = 500;

export function FiltroDeColuna<T>({
  titulo,
  ancora,
  linhas,
  extrair,
  marcados,
  aoAplicar,
  aoOrdenar,
  aoFechar,
}: {
  titulo: string;
  ancora: DOMRect;
  linhas: readonly T[];
  extrair: (linha: T) => string;
  /** `null` = sem filtro nesta coluna (tudo marcado). */
  marcados: string[] | null;
  /** `null` remove o filtro da coluna. */
  aoAplicar: (valores: string[] | null) => void;
  aoOrdenar?: ((desc: boolean) => void) | undefined;
  aoFechar: () => void;
}): JSX.Element {
  const valores = React.useMemo(() => {
    const cont = new Map<string, number>();
    for (const l of linhas) {
      const v = extrair(l);
      cont.set(v, (cont.get(v) ?? 0) + 1);
    }
    // Valor marcado que sumiu pelos outros filtros continua na lista, com 0.
    for (const v of marcados ?? []) if (!cont.has(v)) cont.set(v, 0);
    return [...cont.entries()].sort(([a], [b]) => compararNatural(a, b));
  }, [linhas, extrair, marcados]);

  const [selecao, definirSelecao] = React.useState<Set<string>>(
    () => new Set(marcados ?? valores.map(([v]) => v)),
  );
  const [busca, definirBusca] = React.useState('');
  const termo = busca.trim().toUpperCase();
  const visiveis = termo
    ? valores.filter(([v]) => (v || VAZIO).toUpperCase().includes(termo))
    : valores;
  const mostrados = visiveis.slice(0, MAXIMO_NA_LISTA);
  const todosVisiveisMarcados = visiveis.length > 0 && visiveis.every(([v]) => selecao.has(v));

  const painel = React.useRef<HTMLDivElement | null>(null);
  React.useEffect(() => {
    const fora = (e: MouseEvent): void => {
      if (painel.current && !painel.current.contains(e.target as Node)) aoFechar();
    };
    const tecla = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') aoFechar();
    };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', tecla);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', tecla);
    };
  }, [aoFechar]);

  const aplicar = (): void => {
    // Com busca, como no Excel: fica só o que foi achado E está marcado.
    const escolhidos = termo ? visiveis.map(([v]) => v).filter((v) => selecao.has(v)) : [...selecao];
    const tudo = !termo && valores.every(([v]) => selecao.has(v));
    aoAplicar(tudo ? null : escolhidos);
    aoFechar();
  };

  const alternarTodos = (): void => {
    definirSelecao((atual) => {
      const novo = new Set(atual);
      for (const [v] of visiveis) {
        if (todosVisiveisMarcados) novo.delete(v);
        else novo.add(v);
      }
      return novo;
    });
  };

  const largura = 280;
  const esquerda = Math.max(8, Math.min(ancora.right - largura, window.innerWidth - largura - 8));
  const topo = Math.min(ancora.bottom + 4, window.innerHeight - 420);

  return createPortal(
    <div
      ref={painel}
      role="dialog"
      aria-label={`Filtrar ${titulo}`}
      className="fixed z-[70] flex max-h-[420px] flex-col overflow-hidden rounded-xl border border-borda bg-superficie text-[13px] shadow-forte"
      style={{ left: esquerda, top: Math.max(8, topo), width: largura }}
    >
      {aoOrdenar ? (
        <div className="flex border-b border-borda">
          <button
            type="button"
            onClick={() => {
              aoOrdenar(false);
              aoFechar();
            }}
            className="flex flex-1 items-center gap-1.5 px-3 py-2 text-left hover:bg-superficie2"
          >
            <ArrowDownAZ className="h-4 w-4 text-textoFraco" /> Ordenar A→Z
          </button>
          <button
            type="button"
            onClick={() => {
              aoOrdenar(true);
              aoFechar();
            }}
            className="flex flex-1 items-center gap-1.5 border-l border-borda px-3 py-2 text-left hover:bg-superficie2"
          >
            <ArrowUpAZ className="h-4 w-4 text-textoFraco" /> Z→A
          </button>
        </div>
      ) : null}
      <div className="p-2">
        <label className="relative block">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-textoSuave" />
          <input
            autoFocus
            value={busca}
            onChange={(e) => definirBusca(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') aplicar();
            }}
            placeholder={`Pesquisar em ${titulo.toLowerCase()}`}
            className="h-8 w-full rounded-lg border border-borda bg-superficie pl-8 pr-2 text-[13px] focus:border-primaria focus:outline-none"
          />
        </label>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto border-y border-borda px-1 py-1">
        <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 font-semibold hover:bg-superficie2">
          <input type="checkbox" checked={todosVisiveisMarcados} onChange={alternarTodos} className="h-4 w-4" />
          {termo ? 'Selecionar todos os resultados' : 'Selecionar tudo'}
        </label>
        {mostrados.map(([v, n]) => (
          <label
            key={v}
            className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 hover:bg-superficie2"
          >
            <input
              type="checkbox"
              checked={selecao.has(v)}
              onChange={() =>
                definirSelecao((atual) => {
                  const novo = new Set(atual);
                  if (novo.has(v)) novo.delete(v);
                  else novo.add(v);
                  return novo;
                })
              }
              className="h-4 w-4 shrink-0"
            />
            <span className={cn('min-w-0 flex-1 truncate', !v && 'italic text-textoSuave')} title={v || VAZIO}>
              {v || VAZIO}
            </span>
            <span className="tabular shrink-0 text-2xs text-textoSuave">{numero(n)}</span>
          </label>
        ))}
        {visiveis.length > MAXIMO_NA_LISTA ? (
          <p className="px-2 py-1.5 text-2xs text-textoSuave">
            Mostrando {numero(MAXIMO_NA_LISTA)} de {numero(visiveis.length)} valores — pesquise para achar os outros.
          </p>
        ) : null}
        {visiveis.length === 0 ? <p className="px-2 py-2 text-xs text-textoSuave">Nenhum valor encontrado.</p> : null}
      </div>
      <div className="flex items-center gap-2 p-2">
        {marcados ? (
          <Botao
            variante="fantasma"
            tamanho="pequeno"
            onClick={() => {
              aoAplicar(null);
              aoFechar();
            }}
          >
            Limpar filtro
          </Botao>
        ) : null}
        <Botao variante="fantasma" tamanho="pequeno" className="ml-auto" onClick={aoFechar}>
          Cancelar
        </Botao>
        <Botao variante="primaria" tamanho="pequeno" disabled={selecao.size === 0} onClick={aplicar}>
          Aplicar
        </Botao>
      </div>
    </div>,
    document.body,
  );
}
